import { db, collection, query, where, getDocs, auth } from "@/firebase";
import { logDebug, logInfo, logWarn, logError, logSuccess } from "@/utils/secureLogger";

export interface DuplicateCheckResult {
  isDuplicate: boolean;
  reason?: "filename" | "content" | "both";
  matchedDocument?: {
    id: string;
    filename: string;
    topic: string;
    dateGenerated: any;
    type: "summary" | "flashcard";
  };
  message?: string;
  similarityScore?: number;
}

export interface FilenameExistenceResult {
  existsInSummaries: boolean;
  existsInFlashcards: boolean;
  summaryDoc?: {
    id: string;
    filename: string;
    topic: string;
    dateGenerated: any;
  };
  flashcardDoc?: {
    id: string;
    filename: string;
    topic: string;
    dateGenerated: any;
  };
}

class DuplicateDetectionService {
  /**
   * Calculate text similarity using Jaccard similarity coefficient
   * Compares two texts based on word overlap
   * Returns a score between 0 (completely different) and 1 (identical)
   */
  private calculateTextSimilarity(text1: string, text2: string): number {
    // Normalize and tokenize texts
    const normalize = (text: string): Set<string> => {
      return new Set(
        text
          .toLowerCase()
          .replace(/[^\w\s]/g, " ") // Remove punctuation
          .split(/\s+/)
          .filter((word) => word.length > 2) // Ignore very short words
      );
    };

    const words1 = normalize(text1);
    const words2 = normalize(text2);

    // Calculate Jaccard similarity: intersection / union
    const intersection = new Set([...words1].filter((word) => words2.has(word)));
    const union = new Set([...words1, ...words2]);

    if (union.size === 0) return 0;

    const similarity = intersection.size / union.size;

    logDebug("Text similarity calculated", {
      words1Count: words1.size,
      words2Count: words2.size,
      intersectionCount: intersection.size,
      unionCount: union.size,
      similarity: `${(similarity * 100).toFixed(2)}%`,
    });

    return similarity;
  }

  /**
   * Calculate content fingerprint for faster duplicate detection
   * Creates a hash-like signature of the content for quick comparison
   */
  private calculateContentFingerprint(content: string): string {
    // Extract key features of the content
    const normalized = content
      .toLowerCase()
      .replace(/[^\w\s]/g, " ")
      .split(/\s+/)
      .filter((word) => word.length > 3);

    // Sort and take first 50 words for fingerprint
    const signature = normalized.sort().slice(0, 50).join("-");

    return signature;
  }

  /**
   * Check if a file with the same name already exists for the user (TYPE-SPECIFIC)
   * Returns separate flags for summaries and flashcards collections
   * This allows the same file to be used for both summary and flashcards separately
   */
  async checkFilenameExistsByType(
    userId: string,
    filename: string
  ): Promise<FilenameExistenceResult> {
    try {
      if (!filename || filename.trim() === "") {
        return { existsInSummaries: false, existsInFlashcards: false };
      }

      logInfo("Checking filename existence by type", { filename, userId: userId.substring(0, 8) });

      // Normalize filename for comparison (remove file extension)
      const normalizeFilename = (name: string): string => {
        return name
          .toLowerCase()
          .replace(/\.(pdf|txt|docx)$/i, "")
          .trim();
      };

      const normalizedFilename = normalizeFilename(filename);

      let existsInSummaries = false;
      let existsInFlashcards = false;
      let summaryDoc: any = undefined;
      let flashcardDoc: any = undefined;

      // Check summaries collection
      const summariesQuery = query(collection(db, "summaries"), where("user_id", "==", userId));

      const summariesSnapshot = await getDocs(summariesQuery);

      summariesSnapshot.forEach((doc) => {
        const data = doc.data();
        const existingFilename = data.filename || "";

        if (existingFilename && normalizeFilename(existingFilename) === normalizedFilename) {
          existsInSummaries = true;
          summaryDoc = {
            id: doc.id,
            filename: existingFilename,
            topic: data.topic || "Unknown topic",
            dateGenerated: data.date_generated,
          };
          logDebug("Filename found in summaries", {
            filename,
            documentId: doc.id.substring(0, 8),
          });
        }
      });

      // Check flashcards collection
      const flashcardsQuery = query(collection(db, "flashcards"), where("user_id", "==", userId));

      const flashcardsSnapshot = await getDocs(flashcardsQuery);

      flashcardsSnapshot.forEach((doc) => {
        const data = doc.data();
        const existingFilename = data.filename || "";

        if (existingFilename && normalizeFilename(existingFilename) === normalizedFilename) {
          existsInFlashcards = true;
          flashcardDoc = {
            id: doc.id,
            filename: existingFilename,
            topic: data.topic || "Unknown topic",
            dateGenerated: data.date_generated,
          };
          logDebug("Filename found in flashcards", {
            filename,
            documentId: doc.id.substring(0, 8),
          });
        }
      });

      logSuccess("Filename existence check completed", {
        existsInSummaries,
        existsInFlashcards,
      });

      return {
        existsInSummaries,
        existsInFlashcards,
        summaryDoc,
        flashcardDoc,
      };
    } catch (error) {
      logError("Error checking filename existence by type", error);
      return { existsInSummaries: false, existsInFlashcards: false };
    }
  }

  /**
   * Check if a file with the same name already exists for the user
   * Searches in both summaries and flashcards collections
   * @deprecated Use checkFilenameExistsByType for more granular control
   */
  private async checkFilenameExists(
    userId: string,
    filename: string
  ): Promise<DuplicateCheckResult> {
    try {
      if (!filename || filename.trim() === "") {
        return { isDuplicate: false };
      }

      logInfo("Checking for duplicate filename", { filename, userId: userId.substring(0, 8) });

      // Normalize filename for comparison (remove file extension)
      const normalizeFilename = (name: string): string => {
        return name
          .toLowerCase()
          .replace(/\.(pdf|txt|docx)$/i, "")
          .trim();
      };

      const normalizedFilename = normalizeFilename(filename);

      // Check summaries collection
      const summariesQuery = query(
        collection(db, "summaries"),
        where("user_id", "==", userId)
        // Note: We can't use where() on filename directly because Firestore doesn't support
        // case-insensitive queries, so we'll filter in memory
      );

      const summariesSnapshot = await getDocs(summariesQuery);
      let duplicateFound = false;
      let matchedDoc: any = null;

      // Check summaries
      summariesSnapshot.forEach((doc) => {
        const data = doc.data();
        const existingFilename = data.filename || "";

        if (existingFilename && normalizeFilename(existingFilename) === normalizedFilename) {
          duplicateFound = true;
          matchedDoc = {
            id: doc.id,
            filename: existingFilename,
            topic: data.topic || "Unknown topic",
            dateGenerated: data.date_generated,
            type: "summary" as const,
          };
          logWarn("Duplicate filename found in summaries", {
            filename,
            documentId: doc.id.substring(0, 8),
          });
        }
      });

      if (duplicateFound) {
        return {
          isDuplicate: true,
          reason: "filename",
          matchedDocument: matchedDoc,
          message: `You have already uploaded a file named "${
            matchedDoc.filename
          }" on ${this.formatDate(
            matchedDoc.dateGenerated
          )}. Please rename your file or remove the previous one before uploading.`,
        };
      }

      // Check flashcards collection
      const flashcardsQuery = query(collection(db, "flashcards"), where("user_id", "==", userId));

      const flashcardsSnapshot = await getDocs(flashcardsQuery);

      // Check flashcards
      flashcardsSnapshot.forEach((doc) => {
        const data = doc.data();
        const existingFilename = data.filename || "";

        if (existingFilename && normalizeFilename(existingFilename) === normalizedFilename) {
          duplicateFound = true;
          matchedDoc = {
            id: doc.id,
            filename: existingFilename,
            topic: data.topic || "Unknown topic",
            dateGenerated: data.date_generated,
            type: "flashcard" as const,
          };
          logWarn("Duplicate filename found in flashcards", {
            filename,
            documentId: doc.id.substring(0, 8),
          });
        }
      });

      if (duplicateFound) {
        return {
          isDuplicate: true,
          reason: "filename",
          matchedDocument: matchedDoc,
          message: `You have already uploaded a file named "${
            matchedDoc.filename
          }" on ${this.formatDate(
            matchedDoc.dateGenerated
          )} for flashcards. Please rename your file or remove the previous one before uploading.`,
        };
      }

      logSuccess("No duplicate filename found");
      return { isDuplicate: false };
    } catch (error) {
      logError("Error checking filename duplicates", error);
      // Don't block upload if check fails
      return { isDuplicate: false };
    }
  }

  /**
   * Check if content with 90% or higher similarity already exists for the user
   * Uses Jaccard similarity to compare content
   * ✅ CRITICAL: Excludes post-tests from duplicate checking
   * Post-tests are separate assessments and should not block summary/flashcard generation
   */
  private async checkContentSimilarity(
    userId: string,
    content: string,
    threshold: number = 0.9
  ): Promise<DuplicateCheckResult> {
    try {
      logInfo("Checking for duplicate content (excluding post-tests)", {
        userId: userId.substring(0, 8),
        contentLength: content.length,
        threshold: `${(threshold * 100).toFixed(0)}%`,
      });

      // Calculate fingerprint for quick comparison
      const newFingerprint = this.calculateContentFingerprint(content);

      // Check summaries collection
      const summariesQuery = query(collection(db, "summaries"), where("user_id", "==", userId));

      const summariesSnapshot = await getDocs(summariesQuery);
      let maxSimilarity = 0;
      let mostSimilarDoc: any = null;

      // Check each summary's student_notes
      summariesSnapshot.forEach((doc) => {
        const data = doc.data();
        const existingContent = data.student_notes || "";

        if (existingContent && existingContent.length > 100) {
          // Only compare with substantial content
          const existingFingerprint = this.calculateContentFingerprint(existingContent);

          // Quick fingerprint check first (optimization)
          if (newFingerprint === existingFingerprint) {
            maxSimilarity = 1.0; // 100% match
            mostSimilarDoc = {
              id: doc.id,
              filename: data.filename || "Pasted Notes",
              topic: data.topic || "Unknown topic",
              dateGenerated: data.date_generated,
              type: "summary" as const,
            };
            return;
          }

          // Calculate detailed similarity for close matches
          const similarity = this.calculateTextSimilarity(content, existingContent);

          if (similarity > maxSimilarity) {
            maxSimilarity = similarity;
            if (similarity >= threshold) {
              mostSimilarDoc = {
                id: doc.id,
                filename: data.filename || "Pasted Notes",
                topic: data.topic || "Unknown topic",
                dateGenerated: data.date_generated,
                type: "summary" as const,
              };
            }
          }
        }
      });

      // Check flashcards collection
      const flashcardsQuery = query(collection(db, "flashcards"), where("user_id", "==", userId));

      const flashcardsSnapshot = await getDocs(flashcardsQuery);

      // Check each flashcard's student_notes
      flashcardsSnapshot.forEach((doc) => {
        const data = doc.data();
        const existingContent = data.student_notes || "";

        if (existingContent && existingContent.length > 100) {
          const existingFingerprint = this.calculateContentFingerprint(existingContent);

          // Quick fingerprint check first
          if (newFingerprint === existingFingerprint) {
            maxSimilarity = 1.0;
            mostSimilarDoc = {
              id: doc.id,
              filename: data.filename || "Pasted Notes",
              topic: data.topic || "Unknown topic",
              dateGenerated: data.date_generated,
              type: "flashcard" as const,
            };
            return;
          }

          const similarity = this.calculateTextSimilarity(content, existingContent);

          if (similarity > maxSimilarity) {
            maxSimilarity = similarity;
            if (similarity >= threshold) {
              mostSimilarDoc = {
                id: doc.id,
                filename: data.filename || "Pasted Notes",
                topic: data.topic || "Unknown topic",
                dateGenerated: data.date_generated,
                type: "flashcard" as const,
              };
            }
          }
        }
      });

      // ✅ NOTE: We intentionally DO NOT check post_tests collection
      // Post-tests are assessments, not study materials, and should be excluded from duplicate checks
      // This allows users to generate summaries/flashcards from content they've already used for post-tests

      // Log highest similarity found
      logDebug("Content similarity check completed (post-tests excluded)", {
        maxSimilarity: `${(maxSimilarity * 100).toFixed(2)}%`,
        isDuplicate: maxSimilarity >= threshold,
        checkedSummaries: summariesSnapshot.size,
        checkedFlashcards: flashcardsSnapshot.size,
        excludedPostTests: true,
      });

      if (maxSimilarity >= threshold && mostSimilarDoc) {
        logWarn("Duplicate content detected", {
          similarity: `${(maxSimilarity * 100).toFixed(2)}%`,
          documentId: mostSimilarDoc.id.substring(0, 8),
          type: mostSimilarDoc.type,
        });

        return {
          isDuplicate: true,
          reason: "content",
          matchedDocument: mostSimilarDoc,
          similarityScore: maxSimilarity,
          message: `This content is ${(maxSimilarity * 100).toFixed(0)}% similar to your previous ${
            mostSimilarDoc.type
          } "${mostSimilarDoc.topic}" (${this.formatDate(
            mostSimilarDoc.dateGenerated
          )}). Please use different content.`,
        };
      }

      logSuccess("No duplicate content found (post-tests excluded from check)");
      return { isDuplicate: false };
    } catch (error) {
      logError("Error checking content similarity", error);
      // Don't block upload if check fails
      return { isDuplicate: false };
    }
  }

  /**
   * Format Firestore timestamp to readable date
   */
  private formatDate(timestamp: any): string {
    try {
      if (!timestamp) return "an earlier date";

      // Handle Firestore timestamp
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch (error) {
      return "an earlier date";
    }
  }

  /**
   * Main duplicate detection function - OPTIMIZED VERSION
   * Checks both filename and content for duplicates
   * Uses type-specific filename checking and Jaccard similarity for content (no AI calls)
   * CRITICAL: Run this BEFORE allowing file upload or content generation
   */
  async checkForDuplicates(
    content: string,
    filename?: string,
    userId?: string,
    targetType?: "summary" | "flashcard"
  ): Promise<DuplicateCheckResult> {
    try {
      logInfo("Starting optimized duplicate detection", {
        hasFilename: !!filename,
        contentLength: content.length,
        hasUserId: !!userId,
        targetType: targetType || "any",
      });

      // Get current user if not provided
      const currentUserId = userId || auth.currentUser?.uid;

      if (!currentUserId) {
        logError("No user ID available for duplicate check");
        // Don't block if we can't authenticate - let other security measures handle it
        return { isDuplicate: false };
      }

      // Check 1: Type-specific filename duplicate (NEW SMART LOGIC)
      if (filename && targetType) {
        const filenameCheck = await this.checkFilenameExistsByType(currentUserId, filename);

        // Block only if filename exists in the TARGET collection
        if (targetType === "summary" && filenameCheck.existsInSummaries) {
          logWarn("Duplicate filename detected in summaries, blocking", {
            filename,
            targetType,
          });
          return {
            isDuplicate: true,
            reason: "filename",
            matchedDocument: {
              ...filenameCheck.summaryDoc!,
              type: "summary",
            },
            message: `You have already generated a summary from a file named "${
              filenameCheck.summaryDoc!.filename
            }" on ${this.formatDate(
              filenameCheck.summaryDoc!.dateGenerated
            )}. Please rename your file or remove the previous summary before generating a new one.`,
          };
        }

        if (targetType === "flashcard" && filenameCheck.existsInFlashcards) {
          logWarn("Duplicate filename detected in flashcards, blocking", {
            filename,
            targetType,
          });
          return {
            isDuplicate: true,
            reason: "filename",
            matchedDocument: {
              ...filenameCheck.flashcardDoc!,
              type: "flashcard",
            },
            message: `You have already generated flashcards from a file named "${
              filenameCheck.flashcardDoc!.filename
            }" on ${this.formatDate(
              filenameCheck.flashcardDoc!.dateGenerated
            )}. Please rename your file or remove the previous flashcards before generating new ones.`,
          };
        }

        logSuccess("No type-specific filename conflict - file can be used", {
          filename,
          targetType,
          existsInSummaries: filenameCheck.existsInSummaries,
          existsInFlashcards: filenameCheck.existsInFlashcards,
        });
      }

      // Check 2: Content similarity (90% threshold) - Uses Jaccard similarity, NO AI CALLS
      const contentCheck = await this.checkContentSimilarity(currentUserId, content, 0.9);
      if (contentCheck.isDuplicate) {
        logWarn("Duplicate content detected (≥90% similar), blocking", {
          similarity: `${(contentCheck.similarityScore! * 100).toFixed(0)}%`,
          reason: contentCheck.reason,
        });
        return contentCheck;
      }

      // All checks passed
      logSuccess("No duplicates detected - content is unique and file is available");
      return { isDuplicate: false };
    } catch (error) {
      logError("Error in duplicate detection", error);
      // Don't block upload if detection system fails
      // Better to allow potential duplicate than block legitimate content
      return { isDuplicate: false };
    }
  }
}

// Lazy singleton instance to avoid circular dependency issues
let duplicateDetectionServiceInstance: DuplicateDetectionService | null = null;

export function getDuplicateDetectionService(): DuplicateDetectionService {
  if (!duplicateDetectionServiceInstance) {
    duplicateDetectionServiceInstance = new DuplicateDetectionService();
  }
  return duplicateDetectionServiceInstance;
}

// For backward compatibility, export a proxy that lazily initializes
export const duplicateDetectionService = new Proxy({} as DuplicateDetectionService, {
  get(target, prop) {
    return getDuplicateDetectionService()[prop as keyof DuplicateDetectionService];
  },
});
export default DuplicateDetectionService;
